// client/src/components/coi/bd/LogLunchDialog.tsx
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useCoiPeople } from "@/hooks/useCois";
import { invalidateActivityViews } from "@/components/activity/ActivityTimeline";
import { occurredAtFromLocalDate } from "@shared/activityTime";
import { MEAL_LABEL } from "@shared/meals";
import { lunchPosts, localYmd, postKey, remainingPosts } from "./lunchPosts";

/** The lunch that never reached the calendar: pick COIs, a day, lunch or meeting, save. */
export function LogLunchDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const people = useCoiPeople();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [meal, setMeal] = useState(true);
  const [date, setDate] = useState(localYmd());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setPicked([]); setSearch(""); setMeal(true); setDate(localYmd()); setNote(""); setSaved(new Set());
  }, [open]);

  const locked = saved.size > 0;
  const rows = people.data ?? [];
  const chosen = rows.filter((r) => picked.includes(r.id));
  const q = search.trim().toLowerCase();
  const matches = rows
    .filter((r) => !picked.includes(r.id) && (!q || [r.name, r.firmName].some((v) => v?.toLowerCase().includes(q))))
    .slice(0, 8);

  const save = async () => {
    setSaving(true);
    const done = new Set(saved);
    let wrote = 0;
    const posts = lunchPosts(
      chosen.map((r) => ({ id: r.id, name: r.name, firmId: r.firmId })),
      { meal, occurredAt: occurredAtFromLocalDate(date), note },
    );
    const todo = remainingPosts(posts, saved);
    try {
      for (const body of todo) {
        const res = await fetch("/api/activities", {
          method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "Couldn't log it");
        done.add(postKey(body));
        wrote++;
      }
      toast.success(meal ? "Lunch logged" : "Meeting logged");
      onOpenChange(false);
    } catch (e: any) {
      const reason = e instanceof TypeError ? "Couldn't reach the server. Check your connection and try again." : e.message;
      const left = posts.filter((p) => !done.has(postKey(p)));
      const ids = new Set(left.flatMap((p) => p.contactIds));
      const names = chosen.filter((r) => ids.has(r.id)).map((r) => r.name).join(", ");
      toast.error(wrote + saved.size > 0
        ? `Saved ${posts.length - left.length} of ${posts.length}. ${reason} Not logged for ${names}. Try again to finish.`
        : reason);
    } finally {
      setSaved(done);
      if (wrote > 0) {
        invalidateActivityViews(qc);
      }
      setSaving(false);
    }
  };

  const toggle = (on: boolean, label: string, onClick: () => void) => (
    <button type="button" onClick={onClick} aria-pressed={on} disabled={locked}
      className={cn("px-3 py-1.5 rounded-lg border text-xs font-medium disabled:opacity-50", on ? "border-primary bg-primary/5 text-primary-text" : "border-border")}>
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Log a lunch or meeting</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">{toggle(meal, MEAL_LABEL, () => setMeal(true))}{toggle(!meal, "Meeting", () => setMeal(false))}</div>
          <div className="space-y-1.5">
            <Label className="text-xs">With</Label>
            {chosen.length > 0 && (
              <div className="flex gap-1.5 flex-wrap">
                {chosen.map((r) => (
                  <span key={r.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-primary bg-primary/10 text-xs">
                    {r.name}
                    <button type="button" aria-label={`Remove ${r.name}`} disabled={locked} onClick={() => setPicked((p) => p.filter((x) => x !== r.id))}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <Input value={search} onChange={(e) => setSearch(e.target.value)} disabled={locked} placeholder="Search COIs" className="h-8 text-sm" />
            <div className="max-h-40 overflow-auto rounded border border-border divide-y divide-border/60">
              {people.isLoading ? <p className="px-2 py-1.5 text-xs text-muted-foreground">Loading…</p>
                : matches.length === 0 ? <p className="px-2 py-1.5 text-xs text-muted-foreground">No matches</p>
                : matches.map((r) => (
                  <button key={r.id} type="button" disabled={locked} onClick={() => { setPicked((p) => [...p, r.id]); setSearch(""); }}
                    className="w-full text-left px-2 py-1.5 text-xs hover:bg-muted/50">
                    <span className="font-medium">{r.name}</span>{r.firmName && <span className="text-muted-foreground">{` · ${r.firmName}`}</span>}
                  </button>
                ))}
            </div>
          </div>
          <div className="space-y-1"><Label className="text-xs">When</Label><Input type="date" value={date} max={localYmd()} disabled={locked} onChange={(e) => setDate(e.target.value)} className="h-8 text-sm w-40" /></div>
          <div className="space-y-1"><Label className="text-xs">Note</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Talked about the Sooner timeline" className="h-8 text-sm" /></div>
          {locked && <p className="text-xs text-muted-foreground">Saved for some firms. Log it again to finish the rest, or close to stop here.</p>}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || chosen.length === 0}>
            {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Log it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
